import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import config from '../../config';

const ResetPassword = ({ token, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Validation du mot de passe
  const passwordChecks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^a-zA-Z0-9]/.test(password)
  };

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isPasswordValid) {
      setError('Le mot de passe ne respecte pas les critères de sécurité');
      return;
    }

    if (!passwordsMatch) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${config.API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Une erreur est survenue');
        return;
      }

      setSuccess(true);

      if (onSuccess) {
        setTimeout(() => onSuccess(), 3000);
      }
    } catch (err) {
      console.error('Erreur reset password:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-success">
        <div className="success-icon">
          <CheckCircle size={48} />
        </div>
        <h3>Mot de passe réinitialisé !</h3>
        <p>
          Votre mot de passe a été modifié avec succès.
          <br /><br />
          Vous allez être redirigé vers la page de connexion...
        </p>
      </div>
    );
  }

  return (
    <div className="reset-password-form">
      <h3>Nouveau mot de passe</h3>
      <p className="form-description">
        Créez un nouveau mot de passe sécurisé pour votre compte.
      </p>

      <form onSubmit={handleSubmit} className="auth-form">
        {error && (
          <div className="auth-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="password">Nouveau mot de passe</label>
          <div className="input-with-icon">
            <Lock size={18} className="input-icon" />
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Nouveau mot de passe"
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {password.length > 0 && (
            <div className="password-requirements">
              <div className={`requirement ${passwordChecks.length ? 'valid' : ''}`}>
                {passwordChecks.length ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                Au moins 8 caractères
              </div>
              <div className={`requirement ${passwordChecks.lowercase ? 'valid' : ''}`}>
                {passwordChecks.lowercase ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                Une lettre minuscule
              </div>
              <div className={`requirement ${passwordChecks.uppercase ? 'valid' : ''}`}>
                {passwordChecks.uppercase ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                Une lettre majuscule
              </div>
              <div className={`requirement ${passwordChecks.number ? 'valid' : ''}`}>
                {passwordChecks.number ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                Un chiffre
              </div>
              <div className={`requirement ${passwordChecks.special ? 'valid' : ''}`}>
                {passwordChecks.special ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                Un caractère spécial
              </div>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
          <div className="input-with-icon">
            <Lock size={18} className="input-icon" />
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirmer le mot de passe"
              required
              autoComplete="new-password"
            />
          </div>
          {confirmPassword.length > 0 && !passwordsMatch && (
            <div className="password-mismatch">
              <AlertCircle size={12} />
              Les mots de passe ne correspondent pas
            </div>
          )}
          {passwordsMatch && (
            <div className="password-match">
              <CheckCircle size={12} />
              Les mots de passe correspondent
            </div>
          )}
        </div>

        <button
          type="submit"
          className="auth-submit-btn"
          disabled={loading || !isPasswordValid || !passwordsMatch}
        >
          {loading ? (
            <>
              <Loader2 size={18} className="spin" />
              Réinitialisation...
            </>
          ) : (
            'Réinitialiser le mot de passe'
          )}
        </button>
      </form>
    </div>
  );
};

export default ResetPassword;
